import { Button } from 'reactstrap';
import { ScopePackRowData } from '../../../../types/portfolio';
import { naturalSort } from '../../../../utils';
import NumberFormat from '../../../../utils/number-format';
import { BasicAlert } from '../../../alert/BasicAlert';
import Table, { TableColumn, TableVariant } from '../../../table/Table';
import { getAnsweredUtrvs, getPackUsagePercentage } from '../../exchange-view/utils';

interface PackUsageTableProps {
  packUsage: ScopePackRowData[] | undefined;
  handleClickScope: (scope: string) => void;
  isQuestionPage: boolean;
}

type ColumnProps = Pick<PackUsageTableProps, 'handleClickScope' | 'isQuestionPage'>;

const sortPercentageColumn = (a: ScopePackRowData, b: ScopePackRowData, type: keyof ScopePackRowData) => {
  const aValue = getPackUsagePercentage(a, type);
  const bValue = getPackUsagePercentage(b, type);
  return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
};

const getTableColumns = ({ handleClickScope, isQuestionPage }: ColumnProps): TableColumn<ScopePackRowData>[] => [
  {
    accessor: 'code',
    Header: () => (isQuestionPage ? 'Code' : 'Survey pack'),
    cellProps: {
      className: 'text-left',
    },
    Cell: (c) => {
      const columnValue = c.row.original.name;
      return isQuestionPage ? (
        columnValue
      ) : (
        <Button color='link' className='text-left' onClick={() => handleClickScope(c.value)}>
          {columnValue}
        </Button>
      );
    },
    sticky: 'left',
  },
  {
    accessor: 'surveyCount',
    Header: () => 'Uses',
    cellProps: {
      className: 'text-center',
    },
  },
  {
    accessor: 'initiativeUniqueCount',
    Header: () => 'Unique uses',
    cellProps: {
      className: 'text-center',
    },
  },
  {
    accessor: 'created',
    Header: () => 'Unanswered',
    cellProps: {
      className: 'text-center',
    },
  },
  {
    accessor: 'updated',
    Header: () => 'Answered',
    cellProps: {
      className: 'text-center',
    },
    Cell: (c) => {
      return getAnsweredUtrvs(c.row.original);
    },
    sortType: (a, b) => naturalSort(getAnsweredUtrvs(a.original).toString(), getAnsweredUtrvs(b.original).toString()),
  },
  {
    accessor: 'verified',
    Header: () => '% Verified',
    cellProps: {
      className: 'text-center',
    },
    Cell: (c) => (
      <NumberFormat value={getPackUsagePercentage(c.row.original, 'verified')} decimalPlaces={1} suffix='%' />
    ),
    sortType: (a, b) => sortPercentageColumn(a.original, b.original, 'verified'),
  },
  {
    accessor: 'isPrivate',
    Header: () => 'Private',
    cellProps: {
      className: 'text-center',
    },
    Cell: (c) => (
      <NumberFormat value={getPackUsagePercentage(c.row.original, 'isPrivate')} decimalPlaces={1} suffix='%' />
    ),
    sortType: (a, b) => sortPercentageColumn(a.original, b.original, 'isPrivate'),
  },
  {
    accessor: 'nr',
    Header: () => 'NA/NR',
    cellProps: {
      className: 'text-center',
    },
    Cell: (c) => <NumberFormat value={getPackUsagePercentage(c.row.original, 'nr')} decimalPlaces={1} suffix='%' />,
    sortType: (a, b) => sortPercentageColumn(a.original, b.original, 'nr'),
  },
];
export default function PackUsageTable(props: PackUsageTableProps) {
  const { packUsage = [], handleClickScope, isQuestionPage = false } = props;

  return (
    <div className='table-wrapper'>
      <Table
        columns={getTableColumns({ handleClickScope, isQuestionPage })}
        data={packUsage}
        pageSize={20}
        sortBy={[{ id: 'code', desc: false }]}
        variant={TableVariant.StickyColumns}
      />
      {packUsage?.length === 0 ? (
        <BasicAlert type='secondary' className='mt-2'>
          No data available for the selected survey packs
        </BasicAlert>
      ) : null}
    </div>
  );
}
